
import { useState, useRef, useEffect } from 'react';
import { ortodonciaTypes } from '../../../mocks/ortodonciaTypes';

export default function TiposOrtodoncia() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);

    const cardWidth = 336;
    const index = Math.round(el.scrollLeft / cardWidth);
    setActiveIndex(Math.min(index, ortodonciaTypes.length - 1));
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    updateScrollState();
    return () => el.removeEventListener('scroll', updateScrollState);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = 336;
    el.scrollBy({ left: direction === 'left' ? -cardWidth : cardWidth, behavior: 'smooth' });
  };

  const scrollToIndex = (index: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = 336;
    el.scrollTo({ left: index * cardWidth, behavior: 'smooth' });
  };

  return (
    <section id="tipos" className="py-24 bg-ice-100">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-petrol-700 mb-4">Tipos de Ortodoncia</h2>
          <p className="text-xl text-petrol-500 max-w-3xl mx-auto">
            Descubre el tratamiento perfecto para tu sonrisa. Ofrecemos las opciones más avanzadas y efectivas.
          </p>
        </div>

        <div className="relative">
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 cursor-pointer"
              aria-label="Anterior"
            >
              <i className="ri-arrow-left-s-line text-2xl text-petrol-700"></i>
            </button>
          )}

          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 cursor-pointer"
              aria-label="Siguiente"
            >
              <i className="ri-arrow-right-s-line text-2xl text-petrol-700"></i>
            </button>
          )}

          <div
            ref={scrollRef}
            className="flex overflow-x-auto gap-6 pb-4 scrollbar-hide snap-x snap-mandatory scroll-smooth"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {ortodonciaTypes.map((type, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-[312px] bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 snap-center cursor-pointer"
              >
                <div className="h-48 w-full rounded-t-3xl overflow-hidden bg-gradient-to-br from-sky-50 to-ice-100 flex items-center justify-center">
                  <div className="w-24 h-24 flex items-center justify-center bg-white rounded-full shadow-lg">
                    <i className={`${type.icon} text-5xl text-olive-500`}></i>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-petrol-700 mb-3">{type.name}</h3>
                  <p className="text-petrol-400 text-sm mb-4 leading-relaxed line-clamp-3">{type.description}</p>
                  <div className="text-olive-500 font-bold text-lg mb-4">Desde {type.price}</div>
                  <a href="#contacto" className="flex items-center space-x-2 text-petrol-700 font-semibold hover:text-olive-500 transition-colors whitespace-nowrap cursor-pointer">
                    <span>Más información</span>
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-center gap-2 mt-6">
          {ortodonciaTypes.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToIndex(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                activeIndex === index
                  ? 'bg-olive-500 w-8'
                  : 'bg-petrol-200 hover:bg-petrol-300'
              }`}
              aria-label={`Ir a tipo ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
